import {useEffect, useMemo, useState, useRef} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';

const useInfiniteScrollControl = ({
                                    currentStateStoreId,
                                    UIContext,
                                    getNewEntities,
                                    getNextEntities,
                                    onEntitiesChange,
                                    skipInitialFetch = false,
                                    ...restProps
                                  }) => {
  const dispatch = useDispatch();
  const [hasMore, setHasMore] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const isFirstRender = useRef(true);

  const UIProps = useMemo(() => {
    return {
      queryParams: UIContext.queryParams,
      setQueryParams: UIContext.setQueryParams,
    };
  }, [UIContext]);

  const createURLSearchParamsInstance = () => new URLSearchParams(window.location.search);
  const currentQueryParams = createURLSearchParamsInstance();

  const {currentState} = useSelector(
    (state) => ({currentState: state[currentStateStoreId]}),
    shallowEqual
  );

  const {pageCount, entities} = currentState;

  const getEntities = (requestedPageNumber) => {
    const paramsFromURL = {};
    const queryParamsFromURL = createURLSearchParamsInstance();
    queryParamsFromURL.forEach((value, key) => {
      if (value) paramsFromURL[key] = value;
    });
    Promise.resolve()
      .then(() => {
        UIProps.setQueryParams({...paramsFromURL});
      })
      .then(() => dispatch(getNewEntities({
        ...paramsFromURL,
        requestedPageNumber,
      })));
  };

  const resetPageAndGetEntities = () => {
    setPageNumber(1);
    getEntities(1);
  };



  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;

      if (skipInitialFetch) {
        return;
      }
    }

    resetPageAndGetEntities();
  }, [currentQueryParams.toString(), getNewEntities, getNextEntities]);

  useEffect(() => {
    if (entities !== null) {
      setHasMore(checkHasMore());
      onEntitiesChange(entities);
    }
  }, [entities]);

  const next = () => {
    const requestedPageNumber = parseInt(pageNumber, 10) + 1;
    return Promise.resolve()
      .then(() => dispatch(getNextEntities({
        ...UIProps.queryParams,
        requestedPageNumber
      })))
      .then(() => setPageNumber(requestedPageNumber));
  };

  const checkHasMore = () => {
    return parseInt(pageCount, 10) > parseInt(pageNumber, 10);
  };

  return {
    next,
    hasMore,
    reset: resetPageAndGetEntities,
    ...restProps
  };
};

export default useInfiniteScrollControl;
